import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { CheckCircle, Library, Lock, Sparkles, ArrowLeft, Loader2 } from 'lucide-react'
import {
  LEARN_LIBRARY_ONE_TIME_GBP,
  LEARN_LIBRARY_ONE_TIME_PAYMENT_URL,
  LEARN_LIBRARY_ONE_TIME_TYL_PAYMENT_URL,
  LEARN_SUBSCRIPTION_CHECKOUT_URL,
  LEARN_SUBSCRIPTION_HIGHLIGHT_TITLE,
  LEARN_SUBSCRIPTION_PRICE_GBP_YEAR,
  LEARN_SUBSCRIPTION_TAGLINE,
} from '../config/subscription'
import { DIGITAL_CONTENT_CONSENT_VERSION, FAIR_USE_POLICY_PATH, LEGAL_CONTACT_EMAIL } from '../config/legal'
import SubscriptionConsentCheckbox from '../components/legal/SubscriptionConsentCheckbox'

export default function SubscribePage() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [consent, setConsent] = useState(false)

  const directStripeUrl = LEARN_SUBSCRIPTION_CHECKOUT_URL.trim()
  const oneTimePaymentUrl = LEARN_LIBRARY_ONE_TIME_PAYMENT_URL.trim()
  const oneTimeTylUrl = LEARN_LIBRARY_ONE_TIME_TYL_PAYMENT_URL.trim()
  const hasOneTimeCheckoutOption = Boolean(oneTimePaymentUrl || oneTimeTylUrl)

  const startCheckout = async () => {
    if (!consent) {
      setError('Please confirm your consent to proceed.')
      return
    }
    setLoading(true)
    setError('')
    const consentTimestamp = new Date().toISOString()
    const siteOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://learn.rondanini.com'
    const termsUrl = `${siteOrigin}/subscription-terms`
    const fairUseUrl = `${siteOrigin}${FAIR_USE_POLICY_PATH}`

    try {
      const res = await fetch('/.netlify/functions/create-learn-subscription-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerEmail: email.trim() || undefined,
          digitalContentConsent: true,
          consentTimestamp,
          consentVersion: DIGITAL_CONTENT_CONSENT_VERSION,
        }),
      })
      const data = await res.json()
      if (data.url && data.sessionId) {
        try {
          await fetch('/.netlify/functions/log-subscription-consent', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              stripeSessionId: data.sessionId,
              customerEmail: email.trim() || undefined,
              digitalContentConsent: true,
              consentTimestamp,
              consentVersion: DIGITAL_CONTENT_CONSENT_VERSION,
              termsUrl,
              fairUseUrl,
            }),
          })
        } catch (logErr) {
          console.error('Consent log failed:', logErr)
        }
        window.location.href = data.url
        return
      }
      setError(data.message || data.error || 'Could not start checkout')
    } catch {
      setError('Network error. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white">
      <div className="max-w-lg mx-auto container-padding py-12">
        <Link
          to="/"
          className="inline-flex items-center text-gold-400 hover:text-gold-300 text-sm mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Home
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-navy-800/80 border border-navy-600 rounded-2xl p-8 shadow-xl"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-xl bg-gold-500/20">
              <Library className="w-8 h-8 text-gold-400" />
            </div>
            <div>
              <h1 className="text-2xl font-serif font-bold">Learn Library</h1>
              <p className="text-gold-400 text-lg font-semibold">
                £{LEARN_SUBSCRIPTION_PRICE_GBP_YEAR} / year
              </p>
            </div>
          </div>

          <p className="text-gray-300 text-sm leading-relaxed mb-6">{LEARN_SUBSCRIPTION_TAGLINE}</p>

          <ul className="space-y-2 text-sm text-gray-300 mb-6">
            <li className="flex gap-2">
              <CheckCircle className="w-4 h-4 text-green-400 shrink-0 mt-0.5" />
              Read minimanuals on learn.rondanini.com for 12 months
            </li>
            <li className="flex gap-2">
              <Lock className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
              No PDF/HTML download entitlement — browser access only
            </li>
            <li className="flex gap-2">
              <Sparkles className="w-4 h-4 text-gold-400 shrink-0 mt-0.5" />
              <span>
                Includes <strong className="text-white">{LEARN_SUBSCRIPTION_HIGHLIGHT_TITLE}</strong> — our flagship
                walkthrough for designing and launching a digital asset
              </span>
            </li>
          </ul>

          {!directStripeUrl && (
            <>
              <label className="block text-xs text-gray-400 mb-1">Email (optional — you can enter it on Stripe)</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="w-full px-4 py-3 rounded-lg bg-navy-900 border border-navy-600 text-white placeholder-gray-500 mb-4 focus:ring-2 focus:ring-gold-500 outline-none"
              />
            </>
          )}

          {directStripeUrl && (
            <p className="text-xs text-amber-200/90 mb-4 border border-amber-600/50 rounded-lg p-3 bg-navy-900/40">
              You are using a direct Stripe Payment Link. Consent metadata cannot be attached automatically — ensure your
              Stripe receipt footer includes the waiver text (see{' '}
              <span className="font-mono text-xs">docs/STRIPE-RECEIPT-FOOTER.txt</span>) and consider migrating to
              on-site checkout for full compliance logging.
            </p>
          )}

          <div className="mb-4">
            <SubscriptionConsentCheckbox checked={consent} onChange={setConsent} />
          </div>

          {error && (
            <p className="text-red-400 text-sm mb-4" role="alert">
              {error}
            </p>
          )}

          {directStripeUrl ? (
            consent ? (
              <a
                href={directStripeUrl}
                rel="noopener noreferrer"
                className="w-full py-4 rounded-lg bg-gold-600 hover:bg-gold-500 text-navy-900 font-bold text-lg flex items-center justify-center gap-2 text-center"
              >
                Continue to Stripe to subscribe
              </a>
            ) : (
              <button
                type="button"
                disabled
                className="w-full py-4 rounded-lg bg-gray-600 text-gray-300 font-bold text-lg cursor-not-allowed"
              >
                Confirm consent above to continue
              </button>
            )
          ) : (
            <button
              type="button"
              onClick={startCheckout}
              disabled={loading || !consent}
              className="w-full py-4 rounded-lg bg-gold-600 hover:bg-gold-500 text-navy-900 font-bold text-lg disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Redirecting…
                </>
              ) : (
                'Continue to secure checkout'
              )}
            </button>
          )}

          {hasOneTimeCheckoutOption && (
            <div className="mt-8 pt-8 border-t border-navy-600">
              <h2 className="text-lg font-serif font-semibold text-white mb-1">Full library — one-time purchase</h2>
              <p className="text-gold-400 text-lg font-semibold mb-3">
                £{LEARN_LIBRARY_ONE_TIME_GBP.toFixed(2)} one payment
              </p>
              <p className="text-gray-300 text-sm leading-relaxed mb-4">
                Buy the entire minimanual catalogue in a single transaction (not per module). You pay once; access is
                through My Library in the browser, subject to our Fair Use &amp; Access Policy.
              </p>
              <p className="text-xs text-amber-200/90 mb-4 border border-amber-600/50 rounded-lg p-3 bg-navy-900/40">
                External payment links (Stripe and/or Tyl) do not attach consent metadata automatically. Use the same
                receipt footer and legal pages as for subscriptions; the checkbox above applies before you continue (both
                options).
              </p>
              {consent ? (
                <div className="flex flex-col gap-3">
                  {oneTimePaymentUrl && (
                    <a
                      href={oneTimePaymentUrl}
                      rel="noopener noreferrer"
                      className="w-full py-3 rounded-lg border-2 border-gold-500 text-gold-400 hover:bg-gold-500/10 font-semibold text-center block"
                    >
                      Pay £{LEARN_LIBRARY_ONE_TIME_GBP.toFixed(2)} on Stripe — full library
                    </a>
                  )}
                  {oneTimeTylUrl && (
                    <a
                      href={oneTimeTylUrl}
                      rel="noopener noreferrer"
                      className="w-full py-3 rounded-lg border-2 border-navy-500 bg-navy-900/50 text-white hover:bg-navy-900 font-semibold text-center block"
                      aria-label={`Pay ${LEARN_LIBRARY_ONE_TIME_GBP.toFixed(2)} pounds for full Learn Library via Tyl by NatWest`}
                    >
                      Pay £{LEARN_LIBRARY_ONE_TIME_GBP.toFixed(2)} with Tyl — full library
                    </a>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  disabled
                  className="w-full py-3 rounded-lg border-2 border-gray-600 text-gray-500 font-semibold cursor-not-allowed"
                >
                  Confirm consent above for one-time purchase
                </button>
              )}
            </div>
          )}

          <p className="text-xs text-gray-500 text-center mt-4">
            After payment, sign in at <Link to="/my-library" className="text-gold-400 underline">My Library</Link> with
            the same email. Subscriptions: cancel from your Stripe customer portal (link in receipt).{' '}
            <Link to="/refund-policy" className="text-gold-400 underline">
              Refund policy
            </Link>
            . Legal:{' '}
            <Link to="/subscription-terms" className="text-gold-400 underline">
              Subscription terms
            </Link>
            ,{' '}
            <Link to="/fair-use" className="text-gold-400 underline">
              Fair use
            </Link>
            . Support:{' '}
            <a href={`mailto:${LEGAL_CONTACT_EMAIL}`} className="text-gold-400 underline">
              {LEGAL_CONTACT_EMAIL}
            </a>
            .
          </p>
        </motion.div>
      </div>
    </div>
  )
}
